/**
 * Funnel Canvas Preview - MA-UI-030 + MA-UI-036 + MA-UI-038 + MA-UI-040 + MA-OPS-044 + MA-UI-054
 * 
 * Interactive chat canvas - hero editing surface.
 * 
 * Features (MA-OPS-044 Phase 3 & 4):
 * - Large preview (580px, centered)
 * - Routing hints under response buttons (→ Step Name)
 * - Path highlighting (hover highlights destination)
 * - Clickable header for appearance editing
 * - Title icon updates instantly when changed
 * - Smooth hover interactions
 * - Auto response area color
 * 
 * Features (MA-UI-054):
 * - AI hover icons on editable elements
 * - Lightweight popover menus for AI actions
 * - Step-level and element-level AI controls
 */

import React, { useState, useMemo, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { resolveThemeTokens } from "@/lib/themeAdapters";  // MA-216: Shared canonical token resolver
import { resolveAppearanceValue, MA305_CALIBRATED_VALUES } from "@/lib/appearanceOverrides";  // MA-305: Override intent
import { resolveHeaderIcon } from "@/lib/headerIcons";  // MA-222: Shared canonical header icon resolver
import { QuickBookingWidget } from "@/components/booking";  // MA-FUNNEL-ARCH-073: Unified booking component
import { 
  Pencil, 
  MessageSquare, 
  ArrowUp,
  ArrowRight,
  Sparkles,
  RefreshCw,
  MessageSquarePlus,
  TrendingUp as ConversionIcon,
  Minimize2,
  MessagesSquare,
  Lightbulb,
  ChevronRight,
  Plus,  // MA-UI-104: For insert response controls
  Square,  // For consent checkbox unchecked
  CheckSquare,  // For consent checkbox checked
  // All heroicons for icon rendering
  Home,
  User,
  Users,
  Heart,
  Star,
  Bell,
  Mail,
  Phone,
  Camera,
  Mic,
  Video,
  Music,
  Image,
  File,
  Folder,
  Calendar,
  Clock,
  MapPin,
  Globe,
  Shield,
  Lock,
  Key,
  CreditCard,
  ShoppingCart,
  Gift,
  Tag,
  Bookmark,
  Flag,
  Award,
  Zap,
  Sun,
  Moon,
  Cloud,
  Coffee,
  Car,
  Plane,
  Building,
  Briefcase,
  Book,
  Target,
  TrendingUp,
  BarChart,
  Activity,
  Cpu,
  Wifi,
  Settings,
  HelpCircle,
  CheckCircle,
  MessageCircle,
  Send,
  ThumbsUp,
  Eye,
  Play,
  Layers,
  Grid,
  Code,
  Database,
  Server,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

// =============================================================================
// Icon Map for dynamic rendering
// =============================================================================
const ICON_MAP = {
  home: Home,
  user: User,
  users: Users,
  heart: Heart,
  star: Star,
  bell: Bell,
  mail: Mail,
  phone: Phone,
  camera: Camera,
  mic: Mic,
  video: Video,
  music: Music,
  image: Image,
  file: File,
  folder: Folder,
  calendar: Calendar,
  clock: Clock,
  "map-pin": MapPin,
  globe: Globe,
  shield: Shield,
  lock: Lock,
  key: Key,
  "credit-card": CreditCard,
  "shopping-cart": ShoppingCart,
  gift: Gift,
  tag: Tag,
  bookmark: Bookmark,
  flag: Flag,
  award: Award,
  zap: Zap,
  sun: Sun,
  moon: Moon,
  cloud: Cloud,
  coffee: Coffee,
  car: Car,
  plane: Plane,
  building: Building,
  briefcase: Briefcase,
  book: Book,
  target: Target,
  "trending-up": TrendingUp,
  "bar-chart": BarChart,
  activity: Activity,
  cpu: Cpu,
  wifi: Wifi,
  settings: Settings,
  "help-circle": HelpCircle,
  "check-circle": CheckCircle,
  "message-circle": MessageCircle,
  send: Send,
  "thumbs-up": ThumbsUp,
  eye: Eye,
  play: Play,
  layers: Layers,
  grid: Grid,
  code: Code,
  database: Database,
  server: Server,
};

// =============================================================================
// MA-FUNNEL-ARCH-073: Mock Slots for Preview Mode
// CRITICAL: Must match API response shape exactly from /api/booking/slots/{calendarId}
// API returns: { today_slots: [...], tomorrow_slots: [...] }
// =============================================================================
const CANVAS_MOCK_SLOTS = {
  today_slots: [
    { slot_id: 'mock_today_1', display_time: '10:30 AM', start_time: '2024-01-01T10:30:00Z', duration_minutes: 15 },
    { slot_id: 'mock_today_2', display_time: '2:00 PM', start_time: '2024-01-01T14:00:00Z', duration_minutes: 15 },
    { slot_id: 'mock_today_3', display_time: '4:30 PM', start_time: '2024-01-01T16:30:00Z', duration_minutes: 15 },
  ],
  tomorrow_slots: [
    { slot_id: 'mock_tomorrow_1', display_time: '9:00 AM', start_time: '2024-01-02T09:00:00Z', duration_minutes: 15 },
    { slot_id: 'mock_tomorrow_2', display_time: '11:00 AM', start_time: '2024-01-02T11:00:00Z', duration_minutes: 15 },
  ],
};

// =============================================================================
// Layout Constants (MA-UI-046)
// =============================================================================
const LAYOUT = {
  PREVIEW_WIDTH: 440,  // Matches Test Page CONTAINER_WIDTH for compact parity
  HEADER_HEIGHT: 52,
  RESPONSE_MAX_WIDTH: 260,  // Max width for response buttons
};

// =============================================================================
// Color Utilities
// =============================================================================

function getLuminance(hexColor) {
  // Handle undefined/null colors with a default
  if (!hexColor || typeof hexColor !== 'string') {
    return 0.5; // Return middle luminance for undefined colors
  }
  
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  const [rs, gs, bs] = [r, g, b].map(c => 
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getContrastingShade(hexColor) {
  // Handle undefined/null colors with a default
  if (!hexColor || typeof hexColor !== 'string') {
    return '#1a1a1a'; // Return default dark color for undefined colors
  }
  
  const luminance = getLuminance(hexColor);
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const factor = luminance < 0.5 ? 1.4 : 0.85;
  
  const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
  const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
  const newB = Math.min(255, Math.max(0, Math.round(b * factor)));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

// Get contrasting text color (black or white) for a background color
// Uses same logic as themeEngine.js contrastSafe() - threshold 0.4
function getContrastingTextColor(hexColor) {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // sRGB gamma correction
  function srgb(c) {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  
  const luminance = 0.2126 * srgb(r) + 0.7152 * srgb(g) + 0.0722 * srgb(b);
  return luminance > 0.4 ? '#000000' : '#ffffff';
}

// =============================================================================
// AI Action Menu Items (MA-UI-054)
// =============================================================================

// Step-level AI actions (for the chat container header)
// MA-UI-118: Changed to element-level actions to edit header title only (matches Bot Message behavior)
const HEADER_AI_ACTIONS = [
  { id: 'improve_clarity', label: 'Improve clarity', icon: Lightbulb },
  { id: 'increase_conversion', label: 'Increase conversion', icon: ConversionIcon },
  { id: 'shorten', label: 'Shorten', icon: Minimize2 },
  { id: 'make_conversational', label: 'Make more conversational', icon: MessagesSquare },
];

// Element-level AI actions (for questions, messages, response buttons)
const ELEMENT_AI_ACTIONS = [
  { id: 'improve_clarity', label: 'Improve clarity', icon: Lightbulb },
  { id: 'increase_conversion', label: 'Increase conversion', icon: ConversionIcon },
  { id: 'shorten', label: 'Shorten', icon: Minimize2 },
  { id: 'make_conversational', label: 'Make more conversational', icon: MessagesSquare },
];

// =============================================================================
// AI Action Popover Component (MA-UI-054 + MA-UI-081)
// =============================================================================
// MA-UI-081: Updated dropdown icon color to match AI button (indigo)
function AIActionPopover({ actions, onAction, position = "right", children }) {
  const [open, setOpen] = useState(false);
  
  const handleAction = (actionId) => {
    onAction?.(actionId);
    setOpen(false);
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        side={position}
        align="start"
        className="w-auto p-1 bg-slate-900/95 backdrop-blur-sm border-white/10"
      >
        <div className="flex flex-col gap-0.5">
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleAction(action.id)}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-200 hover:bg-white/10 rounded-md transition-colors text-left"
              data-testid={`ai-action-${action.id}`}
            >
              {/* MA-UI-081: Icons use indigo color to match AI button */}
              <action.icon className="h-3.5 w-3.5 text-indigo-400" />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// AI Circular Button Component (MA-UI-081)
// =============================================================================
// MA-UI-081: Reverted to circular icon-only design (no pill, no text)
function AICircularButton({ actions, onAction, onClick }) {
  return (
    <AIActionPopover 
      actions={actions} 
      onAction={onAction}
      position="bottom"
    >
      <button
        className={cn(
          "flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200",
          // MA-UI-081: Electric Indigo circular styling
          "bg-indigo-500 hover:bg-indigo-600 text-white",
          // Static shadow
          "shadow-[0_0_8px_rgba(99,102,241,0.4)] hover:shadow-[0_0_12px_rgba(99,102,241,0.6)]",
          "hover:scale-105"
        )}
        data-testid="ai-element-actions-btn"
        onClick={onClick}
      >
        <Sparkles className="h-3 w-3" />
      </button>
    </AIActionPopover>
  );
}

// =============================================================================
// Editable Element Wrapper - MA-UI-081: Floating controls with correct placement
// =============================================================================
// MA-UI-081: Complete refactor for correct control placement and interaction
// - AI button is circular, flush with top edge of bubble, directly beside it
// - AI button clickable on hover without needing to select first
// - System messages (left-aligned): Edit top-left, AI top-right of bubble
// - Prospect responses (right-aligned): Edit top-right, AI top-left of bubble
// - Hover/selection behavior identical for both message types
// - Expanded hover region ensures controls remain accessible
function EditableElement({ 
  children, 
  elementType, 
  elementId, 
  isSelected, 
  onSelect, 
  editLabel = "Edit",
  onAIAction,
  showAIButton = true,
  // MA-UI-081: Control placement based on message alignment
  // "left" = left-aligned bubble (system msg): Edit top-left, AI top-right
  // "right" = right-aligned bubble (prospect): Edit top-right, AI top-left
  alignment = "left",
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  const handleAIAction = (actionId) => {
    onAIAction?.({ elementType, elementId, actionId });
  };
  
  const isLeftAligned = alignment === "left";
  const showControls = isHovered || isSelected;
  
  return (
    // MA-UI-081: Outer wrapper with expanded hover region for control accessibility
    // The padding creates an invisible hover zone that includes the AI button area
    <div
      className={cn(
        "relative inline-block",
        // Expanded hover region: extra padding on the side where AI button appears
        // This ensures cursor can move to AI button without losing hover
        isLeftAligned ? "pr-10" : "pl-10",
        // Top padding for Edit button
        "pt-4",
        // Negative margin to compensate for padding (maintains visual position)
        isLeftAligned ? "-mr-10" : "-ml-10",
        "-mt-4"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Inner container that holds actual content */}
      <div className="relative inline-block">
        {/* MA-UI-081: Edit button - floats above bubble corner */}
        {showControls && (
          <div 
            className={cn(
              "absolute z-30 -top-3",
              // Position based on alignment: left-aligned = top-left, right-aligned = top-right
              isLeftAligned ? "-left-1" : "-right-1"
            )}
          >
            <div 
              className={cn(
                "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
                "bg-slate-800/90 backdrop-blur-sm",
                "border border-slate-600/50",
                "text-slate-200 text-[10px] font-medium",
                "hover:bg-slate-700",
                "shadow-md",
                "transition-all duration-150"
              )}
              onClick={(e) => {
                e.stopPropagation();
                onSelect?.({ type: elementType, id: elementId });
              }}
              data-testid={`edit-btn-${elementType}-${elementId}`}
            >
              <Pencil className="h-2.5 w-2.5" />
              <span>{editLabel}</span>
            </div>
          </div>
        )}
        
        {/* MA-UI-081: AI circular button - flush with top edge, directly beside bubble */}
        {showControls && showAIButton && (
          <div 
            className={cn(
              "absolute z-30 top-0",
              // Position based on alignment: 
              // left-aligned (system) = right side of bubble
              // right-aligned (prospect) = left side of bubble
              isLeftAligned ? "left-full ml-1.5" : "right-full mr-1.5"
            )}
          >
            <AICircularButton
              actions={ELEMENT_AI_ACTIONS}
              onAction={handleAIAction}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        
        {/* Bubble content - MA-UI-109: Added pop-out effect matching booking sections */}
        <div
          className={cn(
            "relative cursor-pointer rounded-2xl",
            "transition-all duration-200 ease-out",
            // MA-UI-109: Pop-out effect on hover (same as booking sections)
            isHovered && !isSelected && [
              "shadow-[0_4px_12px_rgba(99,102,241,0.15)]", // Soft indigo shadow
              "-translate-y-0.5", // Slight upward movement
            ],
            // MA-UI-109: Stronger pop-out when selected
            isSelected && [
              "shadow-[0_4px_16px_rgba(99,102,241,0.25)]", // Stronger shadow when selected
            ]
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.({ type: elementType, id: elementId });
          }}
          data-testid={`editable-${elementType}-${elementId}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Clickable Chat Header - with dynamic icon rendering (MA-UI-054 + MA-UI-081)
// =============================================================================
// MA-UI-081: Header AI button now uses circular icon-only design, matching message buttons
// MA-UI-080: Header controls persist when selected, supports visibility toggles
// MA-UI-109: hideAIButton prop to hide AI button for Booking/Consent steps
// MA-184: Accept header prop for top-level header title
// MA-224: Accept userTextColor for canonical icon color
// MA-305: Accept headerBackgroundColor prop for light mode calibration override
function ChatHeader({ 
  appearance, 
  header = null, 
  onHeaderClick, 
  containerBgColor, 
  userTextColor = "#ffffff", 
  onAIAction, 
  isSelected = false, 
  hideAIButton = false, 
  lpTokens = null,
  headerBackgroundColor: propHeaderBg = null  // MA-305: Optional calibrated header bg
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  // MA-234: STRICT CANONICAL READ (no fallback chains)
  // header.* is the ONLY source for content
  // appearance.* is styling ONLY
  const title = header?.title ?? "";
  
  // MA-269: Canonical precedence - appearance → lpTokens (NO hardcoded fallbacks)
  // Backend contract (MA-267) guarantees appearance fields exist
  const userColor = appearance?.userColor ?? appearance?.prospectColor ?? lpTokens?.accent;
  // MA-305: Use prop override if provided (for light mode calibration), otherwise derive from appearance
  const headerBackgroundColor = propHeaderBg ?? appearance?.headerBackgroundColor ?? lpTokens?.cardBg ?? containerBgColor;
  const iconBackgroundColor = appearance?.iconBackgroundColor ?? lpTokens?.accent;
  const showHeaderTitle = appearance?.showHeaderTitle !== false;
  const showHeaderIcon = appearance?.showHeaderIcon !== false;
  
  const customIconUrl = appearance?.customIconUrl;
  
  const bgLuminance = getLuminance(headerBackgroundColor);
  const textColor = bgLuminance < 0.5 ? "#FFFFFF" : "#111827";
  
  // MA-222: Use shared canonical header icon resolver (matches PPB Funnel Step)
  // MA-234: Resolver now enforces strict header.icon_type precedence
  const HeaderIconComponent = resolveHeaderIcon(header);
  
  const handleStepAIAction = (actionId) => {
    // MA-UI-118: Send as system_message to edit only the header title (like Bot Message behavior)
    onAIAction?.({ elementType: 'system_message', elementId: 'header_title', actionId });
  };
  
  // Show controls on hover OR when selected
  const showControls = isHovered || isSelected;
  
  return (
    <div 
      className={cn(
        "relative flex items-center justify-between px-4 cursor-pointer",
        "transition-all duration-200"
      )}
      style={{ 
        height: `${LAYOUT.HEADER_HEIGHT}px`,
        backgroundColor: headerBackgroundColor,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',  // MA-305: Soft divider (parity with PPB Funnel Step)
        // Subtle glow on hover only - no selection border (controls persist instead)
        boxShadow: isHovered && !isSelected
          ? "inset 0 0 12px rgba(99, 102, 241, 0.15)"
          : "none"
      }}
      onClick={onHeaderClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="chat-header"
    >
      <div className="flex items-center gap-3">
        {/* MA-222: Header icon with canonical resolver (matches PPB Funnel Step) */}
        {/* MA-UI-080: Conditionally show icon based on showHeaderIcon */}
        {showHeaderIcon && (() => {
          console.log('🔴 [MA-236] ICON RENDER BLOCK');
          console.log('🔴 [MA-236] customIconUrl:', customIconUrl);
          console.log('🔴 [MA-236] HeaderIconComponent:', HeaderIconComponent);
          console.log('🔴 [MA-236] title:', title);
          return true;
        })() && (
          <>
            {customIconUrl ? (
              <img 
                src={customIconUrl} 
                alt="Icon" 
                className="w-8 h-8 rounded-lg object-contain"
                style={{ backgroundColor: iconBackgroundColor }}
              />
            ) : HeaderIconComponent ? (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: iconBackgroundColor }}
              >
                <HeaderIconComponent className="h-4 w-4" style={{ color: userTextColor }} />
              </div>
            ) : title?.trim() ? (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: iconBackgroundColor }}
              >
                <span className="text-white font-semibold text-sm">
                  {title.charAt(0)}
                </span>
              </div>
            ) : null}
          </>
        )}
        
        {/* MA-UI-080: Conditionally show title based on showHeaderTitle */}
        {showHeaderTitle && (
          <span className="text-sm font-semibold" style={{ color: textColor }}>
            {title}
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {/* MA-UI-081: Step-level AI Button - visible on hover OR when selected */}
        {/* MA-UI-109: Hidden for Booking/Consent steps via hideAIButton prop */}
        {/* MA-UI-118: Uses HEADER_AI_ACTIONS (element-level) to edit header title only */}
        {showControls && !hideAIButton && (
          <AIActionPopover 
            actions={HEADER_AI_ACTIONS}
            onAction={handleStepAIAction}
            position="bottom"
          >
            <button
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200",
                // MA-UI-081: Circular Electric Indigo styling
                "bg-indigo-500 hover:bg-indigo-600 text-white",
                "shadow-[0_0_8px_rgba(99,102,241,0.4)] hover:shadow-[0_0_12px_rgba(99,102,241,0.6)]",
                "hover:scale-105"
              )}
              data-testid="ai-step-actions-header-btn"
            >
              <Sparkles className="h-3 w-3" />
            </button>
          </AIActionPopover>
        )}
        
        {/* MA-UI-081: Edit button - visible on hover OR when selected */}
        {showControls && (
          <div className={cn(
            "flex items-center gap-1 px-2 py-0.5 rounded-full",
            "bg-slate-800/80 backdrop-blur-sm",
            "border border-slate-600/50",
            "text-slate-200 text-[10px] font-medium",
            "hover:bg-slate-700/90",
            "shadow-sm",
            "transition-all duration-150"
          )}>
            <Pencil className="h-2.5 w-2.5" />
            <span>Edit</span>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Bot Message Bubble with Inline Editing (MA-UI-079, MA-UI-080)
// =============================================================================
// MA-UI-079: Bot message bubble - left aligned, flush with left edge
// MA-UI-080: Renamed from SystemMessageBubble, supports text color
function BotMessageBubble({ 
  message, 
  id, 
  isSelected, 
  onSelect, 
  botColor = "#E9E9EB",
  botTextColor = "#1F2937",  // MA-UI-080: Text color prop
  onAIAction,
  onInlineEdit,
  isMutating = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(message || "");
  const inputRef = React.useRef(null);
  
  // Update editValue when message prop changes (after mutation success)
  React.useEffect(() => {
    if (!isEditing) {
      setEditValue(message || "");
    }
  }, [message, isEditing]);
  
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (isMutating) return;
    setIsEditing(true);
    setEditValue(message || "");
  };
  
  const handleBlur = async () => {
    setIsEditing(false);
    if (editValue !== message && onInlineEdit) {
      await onInlineEdit({ content: { message: editValue } });
    }
  };
  
  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setEditValue(message || "");
      setIsEditing(false);
    }
  };
  
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  return (
    <div 
      className="flex flex-col items-start gap-1.5"
      style={{ maxWidth: "85%" }}
    >
      {/* MA-UI-079: EditableElement with left alignment - Edit top-left, AI on right */}
      <EditableElement
        elementType="system_message"
        elementId={id}
        isSelected={isSelected}
        onSelect={onSelect}
        editLabel={isEditing ? "Editing" : "Edit"}
        onAIAction={onAIAction}
        alignment="left"
      >
        <div 
          className={cn(
            "px-4 py-2.5 text-sm rounded-2xl rounded-bl-md",
            isEditing && "ring-2 ring-indigo-400"
          )}
          style={{ 
            backgroundColor: botColor,
            color: botTextColor,
          }}
          onDoubleClick={handleDoubleClick}
          data-testid={`message-bubble-${id}`}
        >
          {isEditing ? (
            <textarea
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className="bg-transparent border-none outline-none resize-none text-sm min-w-[200px]"
              style={{ 
                minHeight: "1.5em",
                color: "inherit"
              }}
              rows={Math.max(1, editValue.split("\n").length)}
              data-testid={`inline-edit-message-${id}`}
            />
          ) : (
            <span className={cn("whitespace-pre-wrap", isMutating && "opacity-50")}>
              {message || "Your question here?"}
            </span>
          )}
        </div>
      </EditableElement>
    </div>
  );
}

// Legacy alias for backward compatibility
const SystemMessageBubble = BotMessageBubble;

// =============================================================================
// Insert Response Control (MA-UI-104)
// =============================================================================
// Small "+" button for inserting responses at specific positions
function InsertResponseControl({ 
  position, // "above" or "below"
  onClick,
  isVisible,
}) {
  if (!isVisible) return null;
  
  return (
    <div 
      className={cn(
        "flex justify-center w-full",
        // MA-UI-104: Tight spacing - py-0.5 (2px) for close proximity to button
        "py-0.5",
        // MA-UI-104: High z-index to stay above EditableElement hover regions
        "relative z-40",
        "animate-in fade-in-0 duration-150"
      )}
    >
      {/* MA-UI-104: Expanded hover area wrapper for better responsiveness */}
      <div className="p-2 -m-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className={cn(
            "w-5 h-5 rounded-full flex items-center justify-center",
            // MA-UI-104: Match Edit button colors (slate-800 base, slate-700 hover)
            "bg-slate-800/90 backdrop-blur-sm",
            "border border-slate-600/50",
            "text-slate-300",
            // Obvious highlight on hover - brighter background, white text, glow
            "hover:bg-slate-600 hover:border-slate-400 hover:text-white",
            "hover:shadow-[0_0_8px_rgba(148,163,184,0.4)]",
            "transition-all duration-150",
            "hover:scale-110",
            "cursor-pointer"
          )}
          data-testid={`insert-response-${position}`}
          title={position === "above" ? "Insert response above" : "Insert response below"}
        >
          <Plus className="h-3 w-3" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// User Response Button with Routing Hint and Inline Editing (MA-UI-079, MA-UI-080)
// =============================================================================
// MA-UI-079: User response button - right aligned, flush with right edge
// MA-UI-080: Renamed terminology from Prospect to User
// MA-UI-104: Added insert response controls above and below
function ResponseButton({ 
  label, 
  id, 
  responseIndex,
  isSelected, 
  onSelect, 
  userColor = "#007AFF",  // MA-UI-080: Renamed from prospectColor
  userTextColor = "#FFFFFF",  // MA-UI-080: Text color prop
  style = "filled",
  onAIAction,
  onInlineEdit,
  onInsertResponse,  // MA-UI-104: Handler for inserting responses
  isMutating = false,
  isFirst = false,  // MA-UI-104: Is this the first response in the stack
  isLast = false,   // MA-UI-104: Is this the last response in the stack
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label || "");
  const inputRef = React.useRef(null);
  
  // Update editValue when label prop changes
  React.useEffect(() => {
    if (!isEditing) {
      setEditValue(label || "");
    }
  }, [label, isEditing]);
  
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (isMutating) return;
    setIsEditing(true);
    setEditValue(label || "");
  };
  
  const handleBlur = async () => {
    setIsEditing(false);
    if (editValue !== label && onInlineEdit && responseIndex !== undefined) {
      await onInlineEdit(responseIndex, { label: editValue });
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setEditValue(label || "");
      setIsEditing(false);
    }
  };
  
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  return (
    // MA-UI-103: Hover handling moved to outer container for stable routing hint display
    // MA-UI-104: Container now includes insert controls above and below
    <div 
      className="flex flex-col items-end"
      style={{ maxWidth: `${LAYOUT.RESPONSE_MAX_WIDTH}px` }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* MA-UI-104: Insert control above (only for first response or when hovering) */}
      <InsertResponseControl
        position="above"
        isVisible={isHovered && onInsertResponse}
        onClick={() => onInsertResponse?.(responseIndex)}
      />
      
      {/* MA-UI-079: EditableElement with right alignment - Edit top-right, AI on left */}
      <EditableElement
        elementType="response_button"
        elementId={id}
        isSelected={isSelected}
        onSelect={onSelect}
        editLabel={isEditing ? "Editing" : "Edit"}
        onAIAction={onAIAction}
        alignment="right"
      >
        <button
          className={cn(
            "px-4 py-2 text-sm font-medium rounded-2xl rounded-br-md",
            "transition-all duration-150",
            "text-left",
            style === "outline" && "bg-transparent border-2",
            isHovered && !isSelected && "brightness-110 scale-[1.02]",
            isEditing && "ring-2 ring-white/50"
          )}
          style={{ 
            backgroundColor: style === "outline" ? "transparent" : userColor,
            borderColor: userColor,
            color: style === "outline" ? userColor : userTextColor,
            maxWidth: `${LAYOUT.RESPONSE_MAX_WIDTH}px`,
          }}
          onDoubleClick={handleDoubleClick}
          data-testid={`response-button-${id}`}
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              className="bg-transparent border-none outline-none text-sm font-medium w-full min-w-[60px]"
              style={{ color: style === "outline" ? userColor : userTextColor }}
              data-testid={`inline-edit-response-${id}`}
            />
          ) : (
            <span className={isMutating ? "opacity-50" : ""}>
              {label || "Response"}
            </span>
          )}
        </button>
      </EditableElement>
      
      {/* MA-UI-104: Insert control below - positioned right after button for symmetry */}
      <InsertResponseControl
        position="below"
        isVisible={isHovered && onInsertResponse}
        onClick={() => onInsertResponse?.(responseIndex + 1)}
      />
    </div>
  );
}

// =============================================================================
// Booking Widget Editable Section (MA-UI-105, MA-UI-107, MA-UI-108, MA-UI-109)
// =============================================================================
// Wrapper component for selectable booking widget sections
// MA-UI-107: Enhanced with hierarchical hover, left-positioned buttons, lifted highlight
// MA-UI-108: Edit button positioning options: top-right, top-left, contentWidth option
// MA-UI-109: Added AI circular button on right side, flush with top
// MA-FUNNEL-ARCH-089: Removed background overlays for clean visual (no nested-card effect)
function BookingEditableSection({
  children,
  sectionType, // "booking_header" | "booking_today" | "booking_tomorrow" | "booking_card"
  sectionId,
  isSelected,
  onSelect,
  onAIAction,
  editLabel = "Edit",
  editPosition = "top-right", // MA-UI-108: "top-right" or "top-left"
  contentWidth = false, // MA-UI-108: If true, element shrinks to fit content
  isChildHovered = false, // MA-UI-107: If a child is hovered, disable parent highlight
  showAIButton = false, // MA-UI-109: Show AI button for this section
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  // MA-UI-107: Only show controls if hovered AND no child is hovered
  const showControls = (isHovered && !isChildHovered) || isSelected;
  // MA-UI-107: Only show highlight if not a child being hovered
  const showHighlight = (isHovered && !isChildHovered) || isSelected;
  
  // MA-UI-109: Handle AI action
  const handleAIAction = (actionId) => {
    // MA-UI-119: Pass nodeId explicitly for booking sections so the AI handler can find the correct node
    onAIAction?.({ elementType: sectionType, elementId: sectionId, actionId, nodeId: sectionId });
  };
  
  return (
    <div
      className={cn(
        "relative",
        contentWidth && "inline-block", // MA-UI-108: Shrink to fit content
        // MA-UI-109: Add padding on right for AI button area when showAIButton is true
        showAIButton && "pr-8 -mr-8"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.({ type: sectionType, id: sectionId });
      }}
      data-testid={`booking-section-${sectionType}`}
    >
      {/* Edit button - MA-UI-108: Positioned based on editPosition prop */}
      {showControls && (
        <div 
          className={cn(
            "absolute z-30",
            editPosition === "top-left" 
              ? "-top-5 -left-1" // MA-UI-108: Top-left corner, raised higher to not cover text
              : "top-0.5 right-2" // Top-right corner, 2px from top
          )}
        >
          <div 
            className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
              "bg-slate-800/90 backdrop-blur-sm",
              "border border-slate-600/50",
              "text-slate-200 text-[10px] font-medium",
              "hover:bg-slate-700",
              "shadow-md",
              "transition-all duration-150"
            )}
          >
            <Pencil className="h-2.5 w-2.5" />
            <span>{editLabel}</span>
          </div>
        </div>
      )}
      
      {/* Content with lifted highlight effect - MA-UI-107/108: Consistent selection styling */}
      {/* MA-UI-109: AI button positioned relative to this content wrapper */}
      <div className="relative">
        {/* MA-UI-109: AI circular button - positioned to align with top of content area */}
        {showControls && showAIButton && (
          <div className="absolute z-30 top-0 -right-8 translate-x-0">
            <AICircularButton
              actions={ELEMENT_AI_ACTIONS}
              onAction={handleAIAction}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        
        <div 
          className={cn(
            "transition-all duration-200 rounded-2xl",
            // MA-FUNNEL-ARCH-091: rounded-2xl matches QuickBookingWidget's 16px border-radius
            // This ensures shadow follows the widget's rounded shape
            showHighlight && !isSelected && [
              "shadow-[0_4px_12px_rgba(99,102,241,0.15)]",
              "-translate-y-0.5",
            ],
            isSelected && [
              "shadow-[0_4px_16px_rgba(99,102,241,0.25)]",
            ]
          )}
          ref={(el) => {
            if (el && sectionType === 'booking_card') {
              const parent = el.parentElement;
              const widget = el.querySelector('[data-testid="quick-booking-widget"]');
              console.log('🔵 [MA-FUNNEL-ARCH-091] Wrapper with rounded-2xl:', {
                sectionType,
                isSelected,
                showHighlight,
              });
            }
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MA-UI-111: Consent Element-Specific Editable Components
// Each component matches the interaction patterns used elsewhere in the builder:
// - Title/Text: Match BookingEditableSection pattern (no AI button)
// - Button: Match BotMessageBubble pattern (Edit badge top-right, no AI button)
// Highlight boundaries auto-fit to content width with small padding
// =============================================================================

// MA-UI-111: Consent Title - Content-width highlight with padding
// Highlight boundary wraps text only, not full row
function ConsentTitleEditable({
  elementId,
  isSelected,
  onSelect,
  title,
  textColor,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const showControls = isHovered || isSelected;
  const isEmpty = !title || title.trim() === "";
  
  return (
    // MA-UI-111: Outer wrapper allows overflow for edit badge
    <div className="overflow-visible">
      {/* MA-UI-111: inline-block container fits content width */}
      <div
        className="relative inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.({ type: "consent_title", id: elementId });
        }}
        data-testid="consent-element-consent_title"
      >
        {/* MA-UI-111: Edit badge - positioned above text with proper spacing */}
        {showControls && (
          <div className="absolute z-30 -top-5 -right-2">
            <div className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
              "bg-slate-800/90 backdrop-blur-sm border border-slate-600/50",
              "text-slate-200 text-[10px] font-medium hover:bg-slate-700",
              "shadow-md transition-all duration-150"
            )}>
              <Pencil className="h-2.5 w-2.5" />
              <span>Edit</span>
            </div>
          </div>
        )}
        
        {/* MA-UI-111: Title with pop-out effect - shadow only, no background highlight */}
        <div 
          className={cn(
            "transition-all duration-200 rounded-lg px-2 py-1 -mx-2 -my-1", // Small padding buffer
            showControls && !isSelected && [
              "shadow-[0_4px_12px_rgba(99,102,241,0.15)]",
              "-translate-y-0.5",
            ],
            isSelected && [
              "shadow-[0_4px_16px_rgba(99,102,241,0.25)]",
            ]
          )}
        >
          <h4 
            className={cn(
              "font-semibold text-sm whitespace-nowrap",
              isEmpty && "italic opacity-50"
            )}
            style={{ color: textColor || "#1F2937" }}
          >
            {isEmpty ? "Add Consent Title" : title}
          </h4>
        </div>
      </div>
    </div>
  );
}

// MA-UI-111: Consent Text - Content-width highlight with padding
// MA-UI-112: Supports HTML hyperlinks for Privacy Policy, Terms of Use, etc.
// Highlight boundary wraps text block only, not full row
function ConsentTextEditable({
  elementId,
  isSelected,
  onSelect,
  consentText,
  requireCheckbox,
  accentColor,
  textColor,
  inputBg,        // MA-FUNNEL-UI-072: Token for background color parity
  borderColor,    // MA-FUNNEL-UI-072: Token for border color parity
  isLightMode,    // MA-FUNNEL-UI-072: Light/dark mode for fallbacks
}) {
  const [isHovered, setIsHovered] = useState(false);
  const showControls = isHovered || isSelected;
  
  // MA-UI-112: Render consent text with clickable hyperlinks
  // Only allows <a> tags with href, target, rel attributes for security
  const renderConsentText = (text) => {
    if (!text) return <span style={{ color: textColor || "#4B5563" }}>Your consent language here...</span>;
    return (
      <span 
        className="consent-links [&_a]:underline [&_a]:text-[#2563eb] [&_a]:hover:text-[#1d4ed8]"
        style={{ color: textColor || "#4B5563" }}
        dangerouslySetInnerHTML={{ __html: text }}
        onClick={(e) => {
          if (e.target.tagName === 'A') {
            e.stopPropagation();
          }
        }}
      />
    );
  };
  
  return (
    // MA-UI-111: Outer wrapper allows overflow for edit badge
    <div className="overflow-visible">
      {/* MA-UI-111: inline-block container fits content width */}
      <div
        className="relative inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.({ type: "consent_text", id: elementId });
        }}
        data-testid="consent-element-consent_text"
      >
        {/* MA-UI-111: Edit badge - positioned above text */}
        {showControls && (
          <div className="absolute z-30 -top-5 -right-2">
            <div className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
              "bg-slate-800/90 backdrop-blur-sm border border-slate-600/50",
              "text-slate-200 text-[10px] font-medium hover:bg-slate-700",
              "shadow-md transition-all duration-150"
            )}>
              <Pencil className="h-2.5 w-2.5" />
              <span>Edit</span>
            </div>
          </div>
        )}
        
        {/* MA-UI-111: Consent text with gray container matching FB Test Page */}
        {/* MA-UI-112: Renders HTML hyperlinks */}
        {/* MA-FUNNEL-UI-072: Use inputBg token for exact parity with PPB and ConversationFunnel */}
        <div 
          className={cn(
            "transition-all duration-200 rounded-xl px-3 py-3", // Container padding
            showControls && !isSelected && [
              "shadow-[0_4px_12px_rgba(99,102,241,0.15)]",
              "-translate-y-0.5",
            ],
            isSelected && [
              "shadow-[0_4px_16px_rgba(99,102,241,0.25)]",
            ]
          )}
          style={{
            backgroundColor: consentText 
              ? (inputBg || (isLightMode ? '#F2F2F7' : 'rgba(255, 255, 255, 0.08)'))
              : (isLightMode ? '#FEF2F2' : 'rgba(254, 226, 226, 0.1)'),
            border: consentText 
              ? `1px solid ${borderColor || (isLightMode ? '#E5E7EB' : 'rgba(255,255,255,0.1)')}`
              : (isLightMode ? '1px solid #FECACA' : '1px solid rgba(254, 202, 202, 0.3)')
          }}
        >
          {requireCheckbox ? (
            <div className="flex items-start gap-2">
              <Square className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: accentColor || "#6bbfe0" }} />
              <p className="text-xs leading-relaxed">
                {renderConsentText(consentText)}
              </p>
            </div>
          ) : (
            <p className="text-xs leading-relaxed">
              {renderConsentText(consentText)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// MA-UI-110: Consent Button - Matches Bot Message bubble behavior
// Edit badge on top-right (not centered), no AI button
function ConsentButtonEditable({
  elementId,
  isSelected,
  onSelect,
  buttonText,
  userColor,
  userTextColor,
  requireCheckbox,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const showControls = isHovered || isSelected;
  
  return (
    <div
      className="relative pt-4 -mt-4" // Extra padding for edit badge visibility
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.({ type: "consent_button", id: elementId });
      }}
      data-testid="consent-element-consent_button"
    >
      {/* MA-UI-110: Edit badge - top-right position like Bot Message bubbles */}
      {showControls && (
        <div className="absolute z-30 -top-0 -right-1">
          <div className={cn(
            "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
            "bg-slate-800/90 backdrop-blur-sm border border-slate-600/50",
            "text-slate-200 text-[10px] font-medium hover:bg-slate-700",
            "shadow-md transition-all duration-150"
          )}>
            <Pencil className="h-2.5 w-2.5" />
            <span>Edit</span>
          </div>
        </div>
      )}
      
      {/* MA-UI-110: Button with pop-out effect matching Bot Message bubbles */}
      <div 
        className={cn(
          "transition-all duration-200 rounded-lg",
          showControls && !isSelected && [
            "shadow-[0_4px_12px_rgba(99,102,241,0.15)]",
            "-translate-y-0.5",
          ],
          isSelected && [
            "shadow-[0_4px_16px_rgba(99,102,241,0.25)]",
          ]
        )}
      >
        <button 
          className={cn(
            "w-full py-2 rounded-lg text-sm font-medium transition-all duration-200",
            requireCheckbox && "opacity-60 cursor-not-allowed"
          )}
          style={{ backgroundColor: userColor, color: userTextColor }}
          data-testid="consent-submit-button"
        >
          {buttonText || "I Agree"}
        </button>
      </div>
    </div>
  );
}

// MA-UI-107/108/109: Heading-only editable wrapper for Today/Tomorrow
// MA-UI-108: Edit button now positioned TOP-LEFT above the heading (like Bot Bubble Messages)
// MA-UI-109: Added AI circular button on right side, flush with top
// MA-FUNNEL-ARCH-089: Removed background overlays for clean visual (no nested-card effect)
function BookingHeadingEditable({
  children,
  sectionType,
  sectionId,
  isSelected,
  onSelect,
  onHoverChange, // Callback to notify parent of hover state
  onAIAction, // MA-UI-109: AI action handler
}) {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const showControls = isHovered || isSelected;
  
  const handleMouseEnter = () => {
    // Clear any pending hide timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
    onHoverChange?.(true);
  };
  
  const handleMouseLeave = () => {
    // Delay hiding to allow cursor travel to AI button/dropdown
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      onHoverChange?.(false);
    }, 300); // 300ms grace period
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  // MA-UI-109: Handle AI action
  const handleAIAction = (actionId) => {
    // MA-UI-119: Pass nodeId explicitly for booking sections so the AI handler can find the correct node
    onAIAction?.({ elementType: sectionType, elementId: sectionId, actionId, nodeId: sectionId });
  };
  
  return (
    <div
      className={cn(
        "relative inline-block" // MA-FUNNEL-ARCH-083: Inline-block to fit content width, parent text-center handles centering
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.({ type: sectionType, id: sectionId });
      }}
      data-testid={`booking-section-${sectionType}`}
    >
      {/* Edit button - MA-UI-108: Positioned TOP-LEFT above the heading, raised higher */}
      {showControls && (
        <div className="absolute z-30 -top-5 -left-1">
          <div 
            className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
              "bg-slate-800/90 backdrop-blur-sm",
              "border border-slate-600/50",
              "text-slate-200 text-[10px] font-medium",
              "hover:bg-slate-700",
              "shadow-md",
              "transition-all duration-150"
            )}
          >
            <Pencil className="h-2.5 w-2.5" />
            <span>Edit</span>
          </div>
        </div>
      )}
      
      {/* Content with lifted highlight effect - MA-UI-108: Matches Header section styling */}
      {/* MA-UI-109: Wrapper for relative positioning of AI button */}
      <div className="relative">
        {/* MA-UI-109: AI circular button - flush with top of padded content, on right side */}
        {/* Offset by -top-0.5 to align with content that has -my-0.5 negative margin */}
        {showControls && (
          <div className="absolute z-30 -top-0.5 -right-8">
            <AICircularButton
              actions={ELEMENT_AI_ACTIONS}
              onAction={handleAIAction}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        
        <div 
          className={cn(
            "transition-all duration-200 px-1.5 py-0.5 -mx-1.5 -my-0.5",
            // MA-FUNNEL-ARCH-091: Removed rounded-lg from heading wrapper
            isHovered && !isSelected && [
              "shadow-[0_4px_12px_rgba(99,102,241,0.15)]",
              "-translate-y-0.5",
            ],
            isSelected && [
              // Selected: stronger shadow for elevated glow effect (no ring)
              "shadow-[0_4px_16px_rgba(99,102,241,0.25)]",
            ]
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// Input Field Preview - Base component (non-editable)
// =============================================================================
function InputFieldPreviewBase({ placeholder, inputType, inputColor = "#007AFF", showArrow = true, inputBg, inputPlaceholder, borderColor }) {
  // MA-UI-108: Placeholder text matching FB Test Page behavior
  const displayPlaceholder = {
    email: "you@example.com",
    phone: "(555) 123-4567",
    name: "John Smith",
    first_name: "John",
    last_name: "Smith",
    zip: "Enter ZIP code",
    text: placeholder || "Type your response...",
  }[inputType] || placeholder || "Type your response...";
  
  // MA-FUNNEL-UI-072: Use inputBg token with fallback for parity
  const bgColor = inputBg || 'rgba(255, 255, 255, 0.8)';
  const placeholderColor = inputPlaceholder || '#9CA3AF';
  
  return (
    <div className="flex items-center gap-2 w-full">
      <input
        type="text"
        placeholder={displayPlaceholder}
        className="flex-1 px-4 py-2 text-sm rounded-lg text-gray-900 outline-none"
        style={{
          backgroundColor: bgColor,
          color: '#1C1C1E',
          border: `1px solid ${borderColor || 'rgba(0, 0, 0, 0.1)'}`
        }}
        readOnly
      />
      {/* MA-PHONE-CONSENT-UI-CORRECTION-042: Only show arrow if NOT a consent step */}
      {showArrow && (
        <button 
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ 
            backgroundColor: inputColor,
            color: getContrastingTextColor(inputColor) 
          }}
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// =============================================================================
// MA-FE-INPUT-UPDATE: Editable Input Field Wrapper
// Provides hover/selection states matching Bot Message bubbles
// Edit badge on top-right, NO AI button as per requirements
// =============================================================================
function InputFieldEditable({
  nodeId,
  placeholder,
  inputType,
  inputColor,
  isSelected,
  onSelect,
  responseAreaColor,
  showArrow = true,  // MA-PHONE-CONSENT-UI-CORRECTION-042
  inputBg,           // MA-FUNNEL-UI-072: Token for input background
  inputPlaceholder,  // MA-FUNNEL-UI-072: Token for placeholder color
  borderColor,       // MA-FUNNEL-UI-072: Token for border color
}) {
  const [isHovered, setIsHovered] = useState(false);
  const showControls = isHovered || isSelected;
  
  return (
    <div 
      className="border-t"
      style={{ backgroundColor: responseAreaColor, borderColor: "rgba(0,0,0,0.06)" }}
    >
      {/* MA-FE-INPUT-UPDATE: Outer wrapper - pill-shaped input is the selectable target, not full row */}
      <div 
        className="px-4 py-3"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* MA-FE-INPUT-UPDATE: The selectable input pill element */}
        <div 
          className={cn(
            "relative cursor-pointer transition-all duration-200 ease-out rounded-full",
            // Pop-out effect on hover (same as Bot Message bubbles)
            isHovered && !isSelected && [
              "shadow-[0_4px_12px_rgba(99,102,241,0.15)]",
              "-translate-y-0.5",
            ],
            // Stronger pop-out when selected
            isSelected && [
              "shadow-[0_4px_16px_rgba(99,102,241,0.25)]",
            ]
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.({ type: "input_field", id: nodeId });
          }}
          data-testid={`input-field-editable-${nodeId}`}
        >
          {/* Edit badge - top-right position like Bot Message bubbles */}
          {showControls && (
            <div className="absolute z-30 -top-3 -right-1">
              <div 
                className={cn(
                  "flex items-center gap-1 px-2 py-0.5 rounded-full cursor-pointer",
                  "bg-slate-800/90 backdrop-blur-sm",
                  "border border-slate-600/50",
                  "text-slate-200 text-[10px] font-medium",
                  "hover:bg-slate-700",
                  "shadow-md",
                  "transition-all duration-150"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect?.({ type: "input_field", id: nodeId });
                }}
                data-testid={`edit-btn-input_field-${nodeId}`}
              >
                <Pencil className="h-2.5 w-2.5" />
                <span>Edit</span>
              </div>
            </div>
          )}
          
          {/* The actual input field preview */}
          <InputFieldPreviewBase 
            placeholder={placeholder} 
            inputType={inputType}
            inputColor={inputColor}
            showArrow={showArrow}  // MA-PHONE-CONSENT-UI-CORRECTION-042
            inputBg={inputBg}
            inputPlaceholder={inputPlaceholder}
            borderColor={borderColor}
          />
        </div>
      </div>
    </div>
  );
}

// Legacy non-editable version for backwards compatibility
function InputFieldPreview({ placeholder, inputType, inputColor, responseAreaColor }) {
  return (
    <div 
      className="flex items-center gap-2 px-4 py-3 border-t"
      style={{ backgroundColor: responseAreaColor, borderColor: "rgba(0,0,0,0.06)" }}
    >
      <InputFieldPreviewBase placeholder={placeholder} inputType={inputType} inputColor={inputColor} />
    </div>
  );
}

// =============================================================================
// Chat Input Area - Always shown at bottom for real chat feel (MA-UI-046)
// =============================================================================
function ChatInputArea({ responseAreaColor }) {
  return (
    <div 
      className="flex items-center gap-2 px-4 py-2.5 border-t"
      style={{ backgroundColor: responseAreaColor, borderColor: "rgba(0,0,0,0.06)" }}
    >
      <input
        type="text"
        placeholder="Type a message..."
        className="flex-1 px-3 py-1.5 text-sm rounded-lg bg-white/60 text-gray-900 placeholder:text-gray-400 border border-gray-200/50 outline-none"
        readOnly
        disabled
      />
      <button 
        className="w-7 h-7 rounded-full bg-gray-200/80 flex items-center justify-center flex-shrink-0"
        disabled
      >
        <Send className="h-3.5 w-3.5 text-gray-400" />
      </button>
    </div>
  );
}

// =============================================================================
// Main Canvas Preview (MA-UI-054: Added AI action callback)
// =============================================================================
export default function FunnelCanvasPreview({
  node,
  nodes = [],
  appearance = {},
  theme = null,           // MA-184: PP theme object
  lpTokens = null,        // MA-184: Converted LP tokens
  header = null,          // MA-184: Top-level header
  selectedElement,
  onElementSelect,
  onDeselect,
  onNavigateToStep,
  onHoverRoute,
  onAIAction,
  onNodeUpdate, // MA-ARCH-076: For inline editing via mutation API
  onInsertResponse, // MA-UI-104: For inserting responses at specific positions
  isMutating = false, // MA-ARCH-076: Disable editing during mutation
}) {
  const [hoveredRouteTarget, setHoveredRouteTarget] = useState(null);
  
  // MA-216: Use shared canonical token resolver (SINGLE SOURCE OF TRUTH)
  // All UI element colors MUST come from resolved map "c" - no inline fallbacks
  
  // MA-218: Runtime data-flow diagnostic logging
  console.log('[MA-218 DEBUG] FunnelCanvasPreview Props:', {
    lpTokens_present: !!lpTokens,
    lpTokens_cardBg: lpTokens?.cardBg,
    lpTokens_surfaceMode: lpTokens?.surfaceMode,
    appearance_containerColor: appearance?.containerColor,
    appearance_threadColor: appearance?.threadColor,
    appearance_botColor: appearance?.botColor,
  });
  
  // MA-304 FIX: Use finalTheme-computed appearance directly when available
  // resolveThemeTokens should only run for legacy cases without finalTheme
  const c =
    appearance?.threadColor &&
    appearance?.containerColor &&
    appearance?.userColor
      ? {
          containerColor: appearance.containerColor,
          threadColor: appearance.threadColor,
          userColor: appearance.userColor,
          botBubbleBg: appearance.botColor || appearance.containerColor,
          botBubbleText: appearance.botTextColor || appearance.textColor,
          userTextColor: appearance.userTextColor || '#ffffff',
          borderColor: appearance.borderColor || 'rgba(0,0,0,0.1)',
          headerText: appearance.headerTextColor || appearance.textColor,
          // MA-FUNNEL-UI-072: Input field tokens for parity with PPB and FB Test Page
          inputBg: appearance.inputBg || (appearance.surfaceMode === 'light' ? '#F2F2F7' : 'rgba(255, 255, 255, 0.08)'),
          inputPlaceholder: appearance.inputPlaceholder || (appearance.surfaceMode === 'light' ? '#8E8E93' : 'rgba(255, 255, 255, 0.5)'),
        }
      : resolveThemeTokens(lpTokens, appearance);
  
  console.log('[MA-218 DEBUG] Resolved tokens from resolveThemeTokens():', {
    containerColor: c.containerColor,
    threadColor: c.threadColor,
    botBubbleBg: c.botBubbleBg,
    userColor: c.userColor,
    lpTokens_was_used: !!lpTokens,
  });
  
  // MA-298: Canvas reference values for parity comparison
  console.log('[MA-298 CANVAS CHECK]', {
    containerColor: c.containerColor,
    threadColor: c.threadColor,
    botBubbleBg: c.botBubbleBg,
    userColor: c.userColor,
    lpTokens_surface1: lpTokens?.surface1,
    lpTokens_cardBg: lpTokens?.cardBg,
    appearance_threadColor: appearance?.threadColor,
    appearance_containerColor: appearance?.containerColor,
  });
  
  // Extract all UI element colors from resolved map
  const containerColor = c.containerColor;
  const threadColor = c.threadColor;
  const userColor = c.userColor;
  const botColor = c.botBubbleBg;  // Canonical bot bubble logic
  const userTextColor = c.userTextColor;
  const botTextColor = c.botBubbleText;
  
  // MA-305: Override intent resolution
  // CRITICAL: appearance[field] is NOT authoritative unless field is in appearance_overrides
  // Treat missing/null appearance_overrides as [] (backward compatible)
  const appearanceOverrides = node?.appearance_overrides || appearance?.appearance_overrides || [];
  const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
  
  console.log('[MA-305-TRACE] FunnelCanvasPreview - Props received:', {
    appearance_keys: Object.keys(appearance || {}),
    appearance_overrides: appearanceOverrides,
    headerBg_from_appearance: appearance?.headerBackgroundColor,
    responseBg_from_appearance: appearance?.responseAreaColor,
    threadBg_from_appearance: appearance?.threadColor,
    isLightMode,
  });
  
  // Resolve ALL appearance-controlled fields through helper (NO direct appearance usage)
  const localThreadColor = resolveAppearanceValue(
    'threadColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { threadColor },
    threadColor,
    isLightMode
  );
  
  const localHeaderBg = resolveAppearanceValue(
    'headerBackgroundColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    {},  // MA-305: Do NOT pass stale appearance values as theme tokens
    containerColor,
    isLightMode
  );
  
  const localResponseBg = resolveAppearanceValue(
    'responseAreaColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    {},  // MA-305: Do NOT pass stale appearance values as theme tokens
    containerColor,
    isLightMode
  );
  
  const localBotBubbleBg = resolveAppearanceValue(
    'botColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { botColor },
    botColor,
    isLightMode
  );
  
  const localContainerColor = resolveAppearanceValue(
    'containerColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { containerColor },
    containerColor,
    isLightMode
  );
  
  const localUserColor = resolveAppearanceValue(
    'userColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { userColor },
    userColor,
    isLightMode
  );
  
  const localUserTextColor = resolveAppearanceValue(
    'userTextColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { userTextColor },
    userTextColor,
    isLightMode
  );
  
  const localBotTextColor = resolveAppearanceValue(
    'botTextColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { botTextColor },
    botTextColor,
    isLightMode
  );
  
  const localIconBg = resolveAppearanceValue(
    'iconBackgroundColor',
    appearance,
    appearanceOverrides,
    MA305_CALIBRATED_VALUES,
    { iconBackgroundColor: c.userColor },
    c.userColor,
    isLightMode
  );
  
  // MA-305: Debug logging AFTER all variables are declared
  console.log('[MA-305-REGRESSION-FIX] Resolved colors:', {
    headerBackgroundColor: localHeaderBg,
    responseAreaColor: localResponseBg,
    threadColor: localThreadColor,
    botColor: localBotBubbleBg,
    hasOverrides: appearanceOverrides?.length > 0,
    overrides: appearanceOverrides,
    isLightMode,
  });
  
  // Use resolved values for rendering (NO direct appearance access)
  const responseAreaColor = localResponseBg;
  const headerBackgroundColor = localHeaderBg;
  const iconBackgroundColor = localIconBg;
  
  const borderColor = c.borderColor;  // Pre-computed from surfaceMode (canonical)
  const headerText = c.headerText;  // Token-based text color (canonical)
  
  // MA-284: STEP 5 - FB Canvas comparison log
  console.log('[MA-284][FB Canvas] botBubbleText:', c.botBubbleText);
  console.log('[MA-284][FB Canvas] resolved c:', c);

  
  // Appearance visibility toggles (not color-related)
  const showHeaderTitle = appearance?.showHeaderTitle !== false;
  const showHeaderIcon = appearance?.showHeaderIcon !== false;
  
  // Legacy compatibility aliases
  const prospectColor = c.userColor;
  const systemColor = c.botBubbleBg;

  // MA-277: Theme debug instrumentation (dev/preview only)
  // MA-277C: Fixed scope - funnel object not available in this component
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugTheme = urlParams.get('debugTheme') === '1';
    
    if (debugTheme) {
      console.log('[FUNNEL_THEME_DEBUG]', {
        surface: 'Builder_Canvas',
        funnelId: node?.id || 'canvas_preview',
        stylingMode: 'themeAdapters',
        surfaceMode: appearance?.surfaceMode || lpTokens?.surfaceMode || 'unknown',
        
        appearance: {
          containerColor: appearance?.containerColor,
          threadColor: appearance?.threadColor,
          headerBackgroundColor: appearance?.headerBackgroundColor,
          userColor: appearance?.userColor,
          botTextColor: appearance?.botTextColor,
          userTextColor: appearance?.userTextColor,
          iconBackgroundColor: appearance?.iconBackgroundColor,
        },
        
        lpTokens: lpTokens ? {
          bgPage: lpTokens.bgPage,
          cardBg: lpTokens.cardBg,
          accent: lpTokens.accent,
          textPrimary: lpTokens.textPrimary,
          textOnAccent: lpTokens.textOnAccent,
          borderSubtle: lpTokens.borderSubtle,
        } : null,
        
        resolved: {
          threadColor,
          botBubbleBg: botColor,
          userColor,
          headerBg: headerBackgroundColor,
          borderColor,
        },
        
        sources: {
          threadColor: appearance?.threadColor ? 'appearance.threadColor' : 'lpTokens.bgPage',
          botBubbleBg: appearance?.containerColor ? 'appearance.containerColor' : 'lpTokens.cardBg',
          userColor: appearance?.userColor ? 'appearance.userColor' : (appearance?.accent ? 'appearance.accent' : 'lpTokens.accent'),
          headerBg: appearance?.containerColor ? 'appearance.containerColor' : 'lpTokens.cardBg',
          borderColor: appearance?.borderColor ? 'appearance.borderColor' : 'default_rgba',
        },
        
        visualModel: {
          separation: 'fill-contrast (no borders on bot bubbles)',
          threadBgValue: threadColor,
          botBubbleValue: botColor,
          contrast: threadColor === botColor ? 'COLLISION' : 'distinct',
        }
      });
    }
  }, [node?.id, appearance, lpTokens, threadColor, botColor, userColor, headerBackgroundColor, borderColor]);
  
  // Build a map of node IDs to labels for routing hints
  const nodeLabels = useMemo(() => {
    const map = {};
    for (const n of nodes) {
      map[n.id] = n.metadata?.label || n.node_type;
    }
    return map;
  }, [nodes]);
  
  // Get route target for a response (from edges)
  const getRouteTarget = (response, nodeEdges = []) => {
    // Check response.target first
    if (response.target) return response.target;
    
    // Then check edges for matching condition
    const responseValue = response.value || response.label?.toLowerCase().replace(/\s+/g, "_");
    for (const edge of nodeEdges) {
      if (edge.condition?.includes(responseValue)) {
        return edge.target_node_id;
      }
    }
    
    // Default to first edge if only one exists
    if (nodeEdges.length === 1) {
      return nodeEdges[0].target_node_id;
    }
    
    return null;
  };
  
  const isElementSelected = (type, id) => {
    return selectedElement?.type === type && selectedElement?.id === id;
  };
  
  const handleHeaderClick = () => {
    onElementSelect?.({ type: "header", id: "appearance" });
  };
  
  const handleCanvasClick = (e) => {
    if (e.target === e.currentTarget) {
      onDeselect?.();
    }
  };
  
  // Handle route hover - propagate to parent for step panel highlighting
  const handleHoverRoute = (routeTarget) => {
    setHoveredRouteTarget(routeTarget);
    if (onHoverRoute) {
      onHoverRoute(routeTarget);
    }
  };
  
  // Handle AI actions from any element (MA-UI-054)
  const handleAIAction = (actionData) => {
    // Enrich with current node context
    const enrichedData = {
      ...actionData,
      nodeId: node?.id,
      nodeType: node?.node_type,
      nodeLabel: node?.metadata?.label,
    };
    onAIAction?.(enrichedData);
  };
  
  // MA-ARCH-076: Handle inline message text edit
  const handleMessageInlineEdit = async (updates) => {
    if (!node || !onNodeUpdate) return;
    
    const result = await onNodeUpdate(updates, {
      nodeId: node.id,
      summary: `Edit ${node.metadata?.label || node.node_type} message`,
    });
    
    return result;
  };
  
  // MA-ARCH-076: Handle inline response label edit
  const handleResponseInlineEdit = async (responseIndex, updates) => {
    if (!node || !onNodeUpdate) return;
    
    const responses = [...(node.content?.responses || [])];
    if (responseIndex >= 0 && responseIndex < responses.length) {
      responses[responseIndex] = { ...responses[responseIndex], ...updates };
      
      const result = await onNodeUpdate(
        { content: { responses } },
        {
          nodeId: node.id,
          summary: `Edit response "${updates.label || 'option'}"`,
        }
      );
      
      return result;
    }
  };
  
  // Render content based on node type
  const renderNodeContent = () => {
    if (!node) {
      return (
        <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: localThreadColor }}>
          <div className="text-center text-gray-400">
            <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Select a step to edit</p>
          </div>
        </div>
      );
    }
    
    const content = node.content || {};
    const nodeType = node.node_type;
    
    switch (nodeType) {
      case "response":  // MA-FE-077: Unified response type
      case "question":  // Legacy question type - same rendering
        return (
          <>
            {/* Message area */}
            <div className="flex-1 px-4 py-4 overflow-y-auto" style={{ backgroundColor: localThreadColor }}>
              <div className="flex flex-col gap-3">
                <BotMessageBubble
                  message={content.message}
                  id={`${node.id}_message`}
                  isSelected={isElementSelected("system_message", `${node.id}_message`)}
                  onSelect={onElementSelect}
                  botColor={localBotBubbleBg}
                  botTextColor={localBotTextColor}
                  onAIAction={handleAIAction}
                  onInlineEdit={handleMessageInlineEdit}
                  isMutating={isMutating}
                />
              </div>
            </div>
            
            {/* Response buttons area */}
            <div 
              className="px-4 py-3 border-t"
              style={{ backgroundColor: responseAreaColor, borderColor: "rgba(0,0,0,0.06)" }}
            >
              <div className="flex flex-col items-end gap-2">
                {(content.responses || []).map((response, idx) => {
                  const routeTarget = getRouteTarget(response, node.edges || []);
                  const routeLabel = routeTarget && !routeTarget.startsWith("__") 
                    ? nodeLabels[routeTarget] 
                    : null;
                  const responsesArray = content.responses || [];
                  
                  return (
                    <ResponseButton
                      key={response.id || idx}
                      label={response.label}
                      id={response.id || `resp_${idx}`}
                      responseIndex={idx}
                      isSelected={isElementSelected("response_button", response.id || `resp_${idx}`)}
                      onSelect={onElementSelect}
                      userColor={localUserColor}
                      userTextColor={localUserTextColor}
                      style={response.style || "filled"}
                      routeTarget={routeTarget}
                      routeLabel={routeLabel}
                      onHoverRoute={handleHoverRoute}
                      onNavigateToStep={onNavigateToStep}
                      onAIAction={handleAIAction}
                      onInlineEdit={handleResponseInlineEdit}
                      onInsertResponse={onInsertResponse ? (insertIndex) => onInsertResponse(node.id, insertIndex) : null}
                      isMutating={isMutating}
                      isFirst={idx === 0}
                      isLast={idx === responsesArray.length - 1}
                    />
                  );
                })}
              </div>
            </div>
          </>
        );
        
      case "input":
        // MA-FE-INPUT-UPDATE: Input field now has hover/selection via InputFieldEditable
        // MA-PHONE-CONSENT-UI-CORRECTION-042: Phone + Consent steps render consent UI inline
        const inputRequiresConsent = content.requires_consent === true;
        const inputConsentNodeId = node.id;
        const inputIsConsentTextSelected = selectedElement?.type === "consent_text" && selectedElement?.id === inputConsentNodeId;
        const inputIsConsentButtonSelected = selectedElement?.type === "consent_button" && selectedElement?.id === inputConsentNodeId;
        const inputRequireCheckbox = content.require_consent_checkbox !== false;
        
        return (
          <>
            <div className="flex-1 px-4 py-4 overflow-y-auto" style={{ backgroundColor: localThreadColor }}>
              <BotMessageBubble
                message={content.message}
                id={`${node.id}_message`}
                isSelected={isElementSelected("system_message", `${node.id}_message`)}
                onSelect={onElementSelect}
                botColor={localBotBubbleBg}
                botTextColor={localBotTextColor}
                onAIAction={handleAIAction}
                onInlineEdit={handleMessageInlineEdit}
                isMutating={isMutating}
              />
            </div>
            
            {/* Input field - no arrow if consent required */}
            <InputFieldEditable 
              nodeId={node.id}
              placeholder={content.placeholder} 
              inputType={content.input_type}
              inputColor={content.input_color || userColor}
              showArrow={!inputRequiresConsent}  // MA-PHONE-CONSENT-UI-CORRECTION-042
              isSelected={isElementSelected("input_field", node.id)}
              onSelect={onElementSelect}
              responseAreaColor={responseAreaColor}
              inputBg={c.inputBg}
              inputPlaceholder={c.inputPlaceholder}
              borderColor={c.borderColor}
            />
            
            {/* MA-PHONE-CONSENT-UI-CORRECTION-042: Consent block (reusing consent node UI) */}
            {inputRequiresConsent && (
              <div 
                className="px-4 py-3"
                style={{ 
                  backgroundColor: responseAreaColor
                }}
              >
                {/* Consent text */}
                <div className="mb-3">
                  <ConsentTextEditable
                    elementId={inputConsentNodeId}
                    isSelected={inputIsConsentTextSelected}
                    onSelect={onElementSelect}
                    consentText={content.consent_text}
                    requireCheckbox={inputRequireCheckbox}
                    accentColor={content.input_color || userColor}
                    textColor={botTextColor}
                    inputBg={c.inputBg}
                    borderColor={c.borderColor}
                    isLightMode={isLightMode}
                  />
                </div>
                
                {/* Submit button */}
                <div>
                  <ConsentButtonEditable
                    elementId={inputConsentNodeId}
                    isSelected={inputIsConsentButtonSelected}
                    onSelect={onElementSelect}
                    buttonText={content.submit_button_text || content.accept_label || "I Agree & Submit"}
                    userColor={lpTokens?.accent || content.input_color || userColor}  // MA-186: PP theme priority
                    userTextColor={lpTokens?.textOnAccent || getContrastingTextColor(content.input_color || userColor)}
                  />
                </div>
              </div>
            )}
          </>
        );
        
      case "message":
        return (
          <>
            <div className="flex-1 px-4 py-4 overflow-y-auto" style={{ backgroundColor: localThreadColor }}>
              <BotMessageBubble
                message={content.message}
                id={`${node.id}_message`}
                isSelected={isElementSelected("system_message", `${node.id}_message`)}
                onSelect={onElementSelect}
                botColor={localBotBubbleBg}
                botTextColor={localBotTextColor}
                onAIAction={handleAIAction}
                onInlineEdit={handleMessageInlineEdit}
                isMutating={isMutating}
              />
              {content.is_terminal && (
                <p className={cn(
                  "text-xs mt-3 ml-1",
                  content.outcome === "qualified" ? "text-green-600" :
                  content.outcome === "not_qualified" ? "text-red-500" :
                  content.outcome === "abandoned" ? "text-amber-500" :
                  content.outcome === "no_consent" ? "text-orange-500" :
                  content.outcome === "error" ? "text-slate-400" :
                  "text-gray-500"
                )} data-testid={`canvas-outcome-${node.id}`}>
                  {content.outcome === "qualified" ? "✓ Qualified" :
                   content.outcome === "not_qualified" ? "✗ Not Qualified" :
                   content.outcome === "abandoned" ? "⊘ Abandoned" :
                   content.outcome === "no_consent" ? "⊘ No Consent" :
                   content.outcome === "error" ? "⚠ Error" :
                   "End of flow"}
                </p>
              )}
            </div>
          </>
        );
        
      case "booking":
        // MA-FUNNEL-ARCH-076: QuickBookingWidget with Canvas text-level editability
        // Uses render props to inject editable wrappers without duplicating UI
        const isBookingCardSelected = selectedElement?.type === "booking_card" && selectedElement?.id === node.id;
        const isBookingHeadingSelected = selectedElement?.type === "booking_heading" && selectedElement?.id === `${node.id}_heading`;
        const isBookingSubheadingSelected = selectedElement?.type === "booking_subheading" && selectedElement?.id === `${node.id}_subheading`;
        const isBookingTodaySelected = selectedElement?.type === "booking_today" && selectedElement?.id === `${node.id}_today`;
        const isBookingTomorrowSelected = selectedElement?.type === "booking_tomorrow" && selectedElement?.id === `${node.id}_tomorrow`;
        
        return (
          <div 
            className="flex-1 px-4 py-4 flex items-center justify-center" 
            style={{ backgroundColor: 'transparent' }}  // MA-FUNNEL-ARCH-101: Transparent to inherit funnel theme
          >
            {/* MA-FUNNEL-ARCH-076: Outer wrapper for booking card selection */}
            {/* Canvas editability: Render props inject BookingHeadingEditable and BookingEditableSection wrappers */}
            <BookingEditableSection
              sectionType="booking_card"
              sectionId={node.id}
              isSelected={isBookingCardSelected}
              onSelect={onElementSelect}
              editLabel="Edit"
              appearance={appearance}
            >
              <QuickBookingWidget
                preview={true}
                mockSlots={CANVAS_MOCK_SLOTS}
                heading={content?.heading || "Schedule Your Consultation"}
                subheading={content?.subheading || "Book directly with a Specialist"}
                showTimezone={content?.config?.show_timezone !== false}
                showDuration={content?.config?.show_duration !== false}
                durationMinutes={content?.config?.duration_minutes}
                showCalendarTitle={content?.config?.show_calendar_title === true}
                calendarTitle={content?.config?.calendar_name}
                tokens={lpTokens}
                accentColor={appearance?.userColor}
                embedded={true}
                contact={{ first_name: 'Demo', last_name: 'User', email: 'demo@example.com', phone: '555-0100' }}
                renderHeading={(text, defaultRender) => (
                  <div style={{ display: 'block', textAlign: 'center' }}>
                    <BookingHeadingEditable
                      sectionType="booking_heading"
                      elementId={`${node.id}_heading`}
                      isSelected={isBookingHeadingSelected}
                      onSelect={onElementSelect}
                      headingText={text}
                      showAIButton={true}
                      onAIAction={handleAIAction}
                      editPosition="top-right"
                    >
                      {defaultRender(text)}
                    </BookingHeadingEditable>
                  </div>
                )}
                
                renderSubheading={(text, defaultRender) => (
                  <div style={{ display: 'block', textAlign: 'center' }}>
                    <BookingHeadingEditable
                      sectionType="booking_subheading"
                      elementId={`${node.id}_subheading`}
                      isSelected={isBookingSubheadingSelected}
                      onSelect={onElementSelect}
                      headingText={text}
                      showAIButton={true}
                      onAIAction={handleAIAction}
                      editPosition="top-right"
                    >
                      {defaultRender(text)}
                    </BookingHeadingEditable>
                  </div>
                )}
                
                renderSectionHeader={(label, section, defaultRender) => (
                  <BookingEditableSection
                    sectionType={`booking_${section}`}
                    sectionId={`${node.id}_${section}`}
                    isSelected={section === "today" ? isBookingTodaySelected : isBookingTomorrowSelected}
                    onSelect={onElementSelect}
                    editLabel="Edit"
                    editPosition="top-left"
                    contentWidth={true}
                    appearance={appearance}
                  >
                    {defaultRender(label, section === "today")}
                  </BookingEditableSection>
                )}
              />
            </BookingEditableSection>
          </div>
        );
        
      case "consent":
        // MA-UI-110: Consent with element-level clickable editing
        // Matches interaction patterns: Title/Text = Booking Header, Button = Bot Message
        const requireCheckbox = content.require_checkbox !== false;
        const consentNodeId = node.id;
        
        // Check which element is selected
        const isConsentTitleSelected = selectedElement?.type === "consent_title" && selectedElement?.id === consentNodeId;
        const isConsentTextSelected = selectedElement?.type === "consent_text" && selectedElement?.id === consentNodeId;
        const isConsentButtonSelected = selectedElement?.type === "consent_button" && selectedElement?.id === consentNodeId;
        
        return (
          <div className="flex-1 p-4 overflow-y-auto" style={{ backgroundColor: localThreadColor }}>
            {/* MA-UI-111: Removed overflow-hidden to prevent Edit badge clipping */}
            <div className="rounded-xl" style={{ backgroundColor: containerColor, border: `1px solid ${borderColor}` }}>
              {/* MA-UI-111: Title Section - Added top padding for Edit badge visibility */}
              <div className="px-4 pt-6 pb-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                <ConsentTitleEditable
                  elementId={consentNodeId}
                  isSelected={isConsentTitleSelected}
                  onSelect={onElementSelect}
                  title={content.title}
                  textColor={botTextColor}
                />
              </div>
              
              {/* MA-UI-110: Consent Language Section (no "Consent Disclosure" label) */}
              <div className="px-4 py-3">
                <ConsentTextEditable
                  elementId={consentNodeId}
                  isSelected={isConsentTextSelected}
                  onSelect={onElementSelect}
                  consentText={content.consent_text}
                  requireCheckbox={requireCheckbox}
                  accentColor={userColor}
                  textColor={botTextColor}
                  inputBg={c.inputBg}
                  borderColor={c.borderColor}
                  isLightMode={isLightMode}
                />
              </div>
              
              {/* MA-UI-110: Submit Button Section */}
              <div className="px-4 py-3" style={{ borderTop: `1px solid ${borderColor}` }}>
                {requireCheckbox && (
                  <p className="text-xs text-red-500 text-center mb-2" data-testid="consent-error-message">
                    Please accept the terms and conditions by checking the box above
                  </p>
                )}
                <ConsentButtonEditable
                  elementId={consentNodeId}
                  isSelected={isConsentButtonSelected}
                  onSelect={onElementSelect}
                  buttonText={content.button_text}
                  userColor={lpTokens?.accent || userColor}  // MA-186: PP theme priority
                  userTextColor={lpTokens?.textOnAccent || userTextColor}
                  requireCheckbox={requireCheckbox}
                />
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="flex-1 px-4 py-4 overflow-y-auto" style={{ backgroundColor: localThreadColor }}>
            <SystemMessageBubble
              message={content.message || "Message content"}
              id={`${node.id}_message`}
              isSelected={isElementSelected("system_message", `${node.id}_message`)}
              onSelect={onElementSelect}
              systemColor={systemColor}
              onAIAction={handleAIAction}
              onInlineEdit={handleMessageInlineEdit}
              isMutating={isMutating}
            />
          </div>
        );
    }
  };
  
  return (
    <div 
      className="flex-1 flex flex-col items-center justify-center p-4 overflow-hidden"
      onClick={handleCanvasClick}
      data-testid="funnel-canvas"
    >
      {/* Chat Container - Large, Centered */}
      <div 
        className="rounded-2xl overflow-hidden shadow-2xl shadow-black/20 flex flex-col"
        style={{ 
          width: `${LAYOUT.PREVIEW_WIDTH}px`,
          maxHeight: node?.node_type === "input" && node?.content?.requires_consent ? "640px" : "520px",
          backgroundColor: localContainerColor,  // MA-305: Use override-aware resolver
        }}
      >
        {/* MA-THEME-PARITY: Hide header for booking step (matches runtime behavior) */}
        {node?.node_type !== "booking" && (
        <ChatHeader 
          appearance={appearance}
          header={header}
          onHeaderClick={handleHeaderClick}
          containerBgColor={containerColor}
          userTextColor={localUserTextColor}
          onAIAction={handleAIAction}
          isSelected={isElementSelected("header", "appearance")}
          hideAIButton={node?.node_type === "consent"}
          lpTokens={lpTokens}
          headerBackgroundColor={headerBackgroundColor}  // MA-305: Pass calibrated header bg
        />
        )}
        {renderNodeContent()}
      </div>
    </div>
  );
}
